import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function MockVSCode({
    activeFile = 'main.py',
    sidebarActive = 'explorer', // explorer, extensions, search
    showTerminal = true,
    showCursorLine = true, // 터미널 하단 커서라인 표시 여부
    highlightSidebar = false,
    highlightExtensions = false,
    highlightTerminal = false,
    highlightPlayButton = false,
    editorContent,
    terminalContent,
    extensionSearchQuery = "",
    extensionItem = null, // { name, publisher, description, icon, version, installed }
    children
}) {
    const terminalRef = useRef(null);

    // Auto-scroll terminal to bottom when content changes
    useEffect(() => {
        if (terminalRef.current) {
            terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
        }
    }, [terminalContent, showTerminal]);

    return (
        <div className="w-full h-[450px] bg-[#1e1e1e] rounded-lg overflow-hidden border border-white/10 shadow-2xl flex flex-col font-mono text-sm relative group">
            {/* Title Bar */}
            <div className="h-9 bg-[#3c3c3c] flex items-center justify-between px-4 shrink-0 select-none">
                <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#ff5f56]"></div>
                    <div className="w-3 h-3 rounded-full bg-[#ffbd2e]"></div>
                    <div className="w-3 h-3 rounded-full bg-[#27c93f]"></div>
                </div>
                <div className="text-slate-400 text-xs font-medium">{activeFile} - Visual Studio Code</div>
                <div className="w-16"></div>
            </div>

            <div className="flex-1 flex overflow-hidden relative">
                {/* Activity Bar */}
                <div className="w-12 bg-[#333333] flex flex-col items-center py-3 gap-4 shrink-0 relative z-20">
                    <div className={`p-2 rounded cursor-pointer transition-colors ${sidebarActive === 'explorer' ? 'text-white border-l-2 border-white' : 'text-slate-500 hover:text-white'}`}>
                        <i className="far fa-copy text-xl"></i>
                    </div>
                    <div className={`p-2 rounded cursor-pointer transition-colors ${sidebarActive === 'search' ? 'text-white border-l-2 border-white' : 'text-slate-500 hover:text-white'}`}>
                        <i className="fas fa-search text-xl"></i>
                    </div>
                    {/* Extensions Icon */}
                    <div className={`p-2 rounded cursor-pointer transition-colors relative ${sidebarActive === 'extensions' ? 'text-white border-l-2 border-white' : 'text-slate-500 hover:text-white'}`}>
                        <i className="fas fa-th-large text-xl"></i>
                    </div>
                    {/* Agent Icon (Visual Only) */}
                    <div className={`p-2 rounded cursor-pointer transition-colors mt-auto mb-2 text-purple-400 hover:text-purple-300`}>
                        <i className="fas fa-robot text-xl"></i>
                    </div>
                </div>

                {/* Sidebar */}
                <AnimatePresence>
                    {sidebarActive !== 'none' && (
                        <div className="w-60 bg-[#252526] border-r border-[#191919] flex flex-col shrink-0">
                            <div className="h-9 px-4 flex items-center text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                                {sidebarActive === 'explorer' && 'Explorer'}
                                {sidebarActive === 'extensions' && 'Extensions'}
                                {sidebarActive === 'search' && 'Search'}
                            </div>
                            <div className="flex-1 p-0 overflow-hidden">
                                {sidebarActive === 'explorer' && (
                                    <div className="space-y-0.5 text-slate-400 text-xs">
                                        <div className="flex items-center gap-1 px-3 py-1 bg-[#37373d] text-white cursor-pointer font-bold border-t border-b border-white/5">
                                            <i className="fas fa-chevron-down text-[10px] w-4 text-center"></i>
                                            <span>MY_PROJECT</span>
                                        </div>
                                        <div className={`pl-6 flex items-center gap-2 px-3 py-1 hover:bg-[#2a2d2e] cursor-pointer ${activeFile.endsWith('.py') ? 'text-yellow-200 bg-[#37373d]/50' : 'text-slate-300'}`}>
                                            <i className="fab fa-python text-yellow-500 w-4 text-center"></i> main.py
                                        </div>
                                        <div className={`pl-6 flex items-center gap-2 px-3 py-1 hover:bg-[#2a2d2e] cursor-pointer ${activeFile.endsWith('.html') ? 'text-orange-200 bg-[#37373d]/50' : 'text-slate-300'}`}>
                                            <i className="fab fa-html5 text-orange-500 w-4 text-center"></i> index.html
                                        </div>
                                        <div className={`pl-6 flex items-center gap-2 px-3 py-1 hover:bg-[#2a2d2e] cursor-pointer ${activeFile.endsWith('.md') ? 'text-blue-200 bg-[#37373d]/50' : 'text-slate-300'}`}>
                                            <i className="fas fa-info-circle text-blue-400 w-4 text-center"></i> README.md
                                        </div>
                                    </div>
                                )}
                                {sidebarActive === 'extensions' && (
                                    <div className="flex flex-col h-full">
                                        <div className="px-3 pb-3 pt-1 border-b border-black/20">
                                            <div className="bg-[#3c3c3c] flex items-center px-2 py-1.5 rounded border border-blue-500/30 ring-1 ring-blue-500/20">
                                                <span className="text-xs text-white">{extensionSearchQuery}</span>
                                                <span className="w-0.5 h-4 bg-blue-400 animate-blink ml-0.5 shrink-0"></span>
                                            </div>
                                        </div>
                                        <div className="flex-1 p-2 overflow-y-auto">
                                            {extensionItem ? (
                                                <div className="bg-[#37373d] p-3 rounded flex gap-3 hover:bg-[#3f3f46] transition-colors group cursor-pointer border border-transparent hover:border-blue-500/30">
                                                    <div className="w-10 h-10 bg-[#333] flex items-center justify-center rounded shrink-0 shadow-inner">
                                                        {extensionItem.icon || <i className="fab fa-python text-blue-400 text-xl"></i>}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex justify-between items-start">
                                                            <div className="text-white text-xs font-bold truncate pr-2">{extensionItem.name}</div>
                                                        </div>
                                                        <div className="text-[10px] text-slate-400 truncate">{extensionItem.description}</div>
                                                        <div className="flex items-center gap-2 mt-2">
                                                            <div className="px-2 py-0.5 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-bold rounded shadow-sm transition-colors">Install</div>
                                                            <span className="text-[9px] text-slate-500"><i className="fas fa-download mr-0.5"></i> 24M</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="p-4 text-center text-xs text-slate-500 italic">
                                                    Search for extensions...
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </AnimatePresence>

                {/* Editor Area */}
                <div className="flex-1 flex flex-col min-w-0 bg-[#1e1e1e] relative">
                    {/* Tabs */}
                    <div className="flex bg-[#252526] overflow-x-auto scrollbar-hide border-b border-[#191919]">
                        <div className="px-4 py-2 bg-[#1e1e1e] text-white text-xs flex items-center gap-2 border-t-2 border-blue-500 min-w-fit pr-8 relative">
                            <i className={`fab ${activeFile.endsWith('py') ? 'fa-python text-yellow-500' : (activeFile.endsWith('html') ? 'fa-html5 text-orange-500' : 'fa-file text-slate-400')}`}></i>
                            {activeFile}
                            <i className="fas fa-times text-slate-500 hover:text-white absolute right-2 text-[10px]"></i>
                        </div>
                    </div>

                    {/* Breadcrumbs & Actions */}
                    <div className="h-8 flex items-center justify-between px-4 bg-[#1e1e1e] text-[11px] text-slate-500 shadow-sm z-10">
                        <div className="flex items-center gap-1.5">
                            <span>my_project</span>
                            <i className="fas fa-chevron-right text-[8px] opacity-70"></i>
                            <span className="text-slate-300">{activeFile}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-md hover:bg-white/10 flex items-center justify-center cursor-pointer transition-all duration-300 ${highlightPlayButton ? 'text-green-400 bg-green-500/20 ring-1 ring-green-500 shadow-[0_0_15px_rgba(74,222,128,0.5)] scale-110 z-50' : 'text-slate-300'}`}>
                                <i className="fas fa-play text-[10px]"></i>
                            </div>
                            <i className="fas fa-columns hover:text-white cursor-pointer"></i>
                            <i className="fas fa-ellipsis-h hover:text-white cursor-pointer"></i>
                        </div>
                    </div>

                    {/* Code Editor */}
                    <div className="flex-1 p-4 overflow-hidden font-mono text-xs leading-relaxed relative bg-[#1e1e1e]">
                        <div className="absolute left-0 top-0 bottom-0 w-12 bg-[#1e1e1e] border-r border-[#333] flex flex-col items-end pr-3 py-4 text-slate-600 select-none font-mono text-[10px]">
                            {Array.from({ length: 25 }).map((_, i) => (
                                <div key={i} className="leading-relaxed">{i + 1}</div>
                            ))}
                        </div>
                        <div className="pl-12 text-slate-300">
                            {editorContent}
                        </div>
                    </div>

                    {/* Terminal Panel */}
                    <AnimatePresence>
                        {showTerminal && (
                            <motion.div
                                initial={{ height: 0 }}
                                animate={{ height: 160 }}
                                exit={{ height: 0 }}
                                className={`border-t border-[#333] bg-[#1e1e1e] flex flex-col relative transition-all duration-300 ${highlightTerminal ? 'shadow-[0_-5px_20px_rgba(59,130,246,0.15)] ring-1 ring-blue-500/30 z-20' : ''}`}
                            >
                                <div className="flex items-center gap-5 px-4 py-2 text-[10px] text-slate-400 uppercase border-b border-[#333] bg-[#252526]">
                                    <span className={`border-b-2 pb-0.5 transition-colors cursor-pointer ${highlightTerminal ? 'text-blue-400 border-blue-500 font-bold' : 'text-white border-white'}`}>Terminal</span>
                                    <span className="hover:text-white cursor-pointer">Output</span>
                                    <span className="hover:text-white cursor-pointer">Debug Console</span>
                                    <div className="ml-auto flex gap-2">
                                        <i className="fas fa-plus hover:text-white cursor-pointer"></i>
                                        <i className="fas fa-trash hover:text-white cursor-pointer"></i>
                                        <i className="fas fa-chevron-down hover:text-white cursor-pointer"></i>
                                    </div>
                                </div>
                                <div
                                    ref={terminalRef}
                                    className="flex-1 p-3 font-mono text-xs text-slate-300 overflow-y-auto custom-scrollbar scroll-smooth"
                                >
                                    <div className="text-slate-500 mb-1">Microsoft Windows [Version 10.0.19045.4291]</div>
                                    <div className="text-slate-500 mb-3">(c) Microsoft Corporation. All rights reserved.</div>
                                    <div className="flex flex-col">
                                        {terminalContent}
                                        {/* Cursor line */}
                                        {showCursorLine && (
                                            <div className="flex items-center gap-1 mt-1">
                                                <span className={`transition-colors ${highlightTerminal ? 'text-blue-400 font-bold' : 'text-green-400'}`}>D:\my_project{'>'}</span>
                                                <span className="animate-pulse bg-slate-500 w-2 h-4 block"></span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* Overlay for any guides */}
            {children}
        </div>
    );
}
